import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import documentApi from "../../../api/documentapi";
import signerApi from "../../../api/signerapi";

import DocumentHeader from "./DocumentHeader";
import { blobToBase64, generateEditedPdf } from "./pdfUtils";
import PdfViewer from "./PdfViewer";
import SignatureSidebar from "./SignatureSidebar";

const RecipientDocumentEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const userEmail = user?.userEmail;

  const { documentId, recipientEmail, documentName, signedFile } =
    location.state || {};

  const [signatureFields, setSignatureFields] = useState([]);
  const [fileBlob, setFileBlob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveDraft, setSaveDraft] = useState(false);
  const [viewportInfo, setViewportInfo] = useState(null);
  const [consentDetails, setConsentDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConsentBrief = async () => {
      try {
        const response = await documentApi.getDocumentDetail(documentId);
        setConsentDetails(response.data);
      } catch (err) {
        setError("Failed to fetch collaboration details.");
        console.error("Error fetching collaboration brief:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchConsentBrief();
  }, [documentId]);

  // Fetch document
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        let blob;

        if (signedFile) {
          const byteCharacters = atob(signedFile);
          const byteArrays = [];
          for (let i = 0; i < byteCharacters.length; i++) {
            byteArrays.push(byteCharacters.charCodeAt(i));
          }
          blob = new Blob([new Uint8Array(byteArrays)], {
            type: "application/pdf",
          });
        } else {
          const response = await documentApi.getDocumentFile(
            documentId,
            userEmail,
          );
          blob = new Blob([response.data], {
            type: "application/pdf",
          });
        }

        const blobUrl = URL.createObjectURL(blob);
        setFileBlob(blobUrl);
      } catch (error) {
        console.error("Error loading document:", error);
      } finally {
        setLoading(false);
      }
    };

    if (documentId || signedFile) fetchDocument();
  }, [documentId, signedFile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const editedPdfBlob = await generateEditedPdf(
        signatureFields,
        fileBlob,
        viewportInfo,
      );

      const base64Pdf = await blobToBase64(editedPdfBlob);

      await signerApi.updateSignerStatus({
        email: recipientEmail || userEmail,
        documentId,
        signStatus: "completed",
        signed_file: base64Pdf,
      });

      toast.success("Document sent successfully!");
      setTimeout(() => navigate("/dashboard/my-docs"), 1500);
    } catch (err) {
      toast.error("Error saving document");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setSaveDraft(true);
    try {
      const editedPdfBlob = await generateEditedPdf(
        signatureFields,
        fileBlob,
        viewportInfo,
      );

      const base64Pdf = await blobToBase64(editedPdfBlob);

      await signerApi.updateSignerStatus({
        email: recipientEmail,
        documentId,
        signStatus: "draft",
        signed_file: base64Pdf,
      });

      toast.success("Draft saved successfully!");
    } catch {
      toast.error("Error saving draft.");
    } finally {
      setSaveDraft(false);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <DocumentHeader
        documentName={documentName}
        saving={saving}
        saveDraft={saveDraft}
        onSave={handleSave}
        onSaveDraft={handleSaveDraft}
        onBack={() => navigate("/dashboard/my-docs")}
      />

      <ToastContainer position="top-right" autoClose={3000} />

      <div style={{ display: "flex" }}>
        <PdfViewer
          fileBlob={fileBlob}
          signatureFields={signatureFields}
          setSignatureFields={setSignatureFields}
          setViewportInfo={setViewportInfo}
          signingMode={consentDetails?.signingMode}
          autoSave={handleSave}
        />

        <SignatureSidebar
          signingMode={consentDetails?.signingMode || "single_page"}
        />
      </div>
    </div>
  );
};

export default RecipientDocumentEditor;
